import React, { useRef, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Printer, Download, Check, X, Minus, AlertTriangle } from 'lucide-react';
import { useInspection, useInspectionResponses, useInspectionTemplateSections, useInspectionTemplateItems } from '@/hooks/useInspections';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

interface InspectionReportProps {
  inspectionId: string;
  onBack: () => void;
}

export const InspectionReport: React.FC<InspectionReportProps> = ({ inspectionId, onBack }) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const { inspection, isLoading } = useInspection(inspectionId);
  const { responses } = useInspectionResponses(inspectionId);
  const { sections } = useInspectionTemplateSections(inspection?.template_id || '');
  const { items } = useInspectionTemplateItems(inspection?.template_id || '');

  const sortedSections = sections?.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)) || [];

  const getResponseForItem = (itemId: string) => {
    return responses?.find(r => r.template_item_id === itemId);
  };

  const getSectionItems = (sectionId: string) => {
    return items?.filter(i => i.section_id === sectionId).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)) || [];
  };

  // Calculate summary stats
  const totalItems = items?.length || 0;
  const passCount = responses?.filter(r => r.response_value === 'PASS' || r.response_value === 'YES').length || 0;
  const failCount = responses?.filter(r => r.response_value === 'FAIL' || r.response_value === 'NO').length || 0;
  const naCount = responses?.filter(r => r.response_value === 'NA').length || 0;
  const score = totalItems - naCount > 0 ? Math.round((passCount / (totalItems - naCount)) * 100) : 0;

  // Chart data
  const pieData = useMemo(() => [
    { name: 'Pass', value: passCount, color: '#16a34a' },
    { name: 'Fail', value: failCount, color: '#dc2626' },
    { name: 'N/A', value: naCount, color: '#6b7280' }
  ].filter(d => d.value > 0), [passCount, failCount, naCount]);

  // Section breakdown data for bar chart
  const sectionData = useMemo(() => {
    return sortedSections.map(section => {
      const sectionItems = getSectionItems(section.id);
      const sectionResponses = sectionItems.map(item => getResponseForItem(item.id));
      
      const sectionPass = sectionResponses.filter(r => r?.response_value === 'PASS' || r?.response_value === 'YES').length;
      const sectionFail = sectionResponses.filter(r => r?.response_value === 'FAIL' || r?.response_value === 'NO').length;
      
      return {
        name: section.name.length > 20 ? section.name.substring(0, 20) + '...' : section.name,
        fullName: section.name,
        pass: sectionPass,
        fail: sectionFail,
        total: sectionItems.length
      };
    });
  }, [sortedSections, items, responses]);

  // Failed items list
  const failedItems = useMemo(() => {
    return responses?.filter(r => r.response_value === 'FAIL' || r.response_value === 'NO').map(response => {
      const item = items?.find(i => i.id === response.template_item_id);
      const section = sections?.find(s => s.id === item?.section_id);
      return {
        section: section?.name || 'Unknown',
        item: item?.item_text || 'Unknown',
        notes: response.notes,
        photos: response.photo_urls
      };
    }) || [];
  }, [responses, items, sections]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const content = reportRef.current;
    if (!content) return;
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Inspection Report</title>
      <style>body{font-family:Arial,sans-serif;margin:30px;color:#000}h1,h2,h3{margin:8px 0}table{width:100%;border-collapse:collapse;margin:12px 0}th,td{padding:8px 12px;border:1px solid #ddd;text-align:left}th{background:#f5f5f5;font-weight:600}.footer{margin-top:30px;text-align:center;font-size:8pt;color:#666;border-top:1px solid #ccc;padding-top:8px}@media print{button{display:none}}</style></head><body>
      ${content.innerHTML}
      <div class="footer">Confidential — For Internal Use Only</div></body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  const handleExportPDF = async () => {
    if (!reportRef.current) return;

    try {
      toast.loading('Generating PDF...');
      
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      const scaledWidth = pdfWidth - 20; // 10mm margin on each side
      const scaledHeight = (imgHeight * scaledWidth) / imgWidth;
      
      let heightLeft = scaledHeight;
      let position = 10; // Start with 10mm top margin
      let page = 1;

      // Add first page
      pdf.addImage(imgData, 'PNG', 10, position, scaledWidth, scaledHeight);
      heightLeft -= (pdfHeight - 20);

      // Add additional pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        page++;
        position = -((page - 1) * (pdfHeight - 20)) + 10;
        pdf.addImage(imgData, 'PNG', 10, position, scaledWidth, scaledHeight);
        heightLeft -= (pdfHeight - 20);
      }

      const fileName = `inspection-${inspection?.site?.name?.replace(/\s+/g, '-') || 'report'}-${format(new Date(inspection?.submitted_at || inspection?.created_at || new Date()), 'yyyy-MM-dd')}.pdf`;
      pdf.save(fileName);
      
      toast.dismiss();
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.dismiss();
      toast.error('Failed to generate PDF');
    }
  };

  const renderResponseBadge = (value: string | null) => {
    if (value === 'PASS' || value === 'YES') {
      return <Badge className="bg-green-600"><Check className="h-3 w-3 mr-1" /> {value}</Badge>;
    }
    if (value === 'FAIL' || value === 'NO') {
      return <Badge variant="destructive"><X className="h-3 w-3 mr-1" /> {value}</Badge>;
    }
    if (value === 'NA') {
      return <Badge variant="secondary"><Minus className="h-3 w-3 mr-1" /> N/A</Badge>;
    }
    return <Badge variant="outline">{value || 'No response'}</Badge>;
  };

  if (isLoading || !inspection) {
    return <div className="flex items-center justify-center p-8">Loading report...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Actions - Not printed */}
      <div className="flex items-center justify-between print:hidden">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button onClick={handleExportPDF}>
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Report Content */}
      <div ref={reportRef} className="space-y-6 bg-background p-6 print:p-0 inspection-report-print">
        {/* Print Header - Only shown in print */}
        <div className="hidden print:block print-header">
          <h1>{inspection.template?.name}</h1>
          <p className="print-date">
            Inspection Report - {format(new Date(inspection.submitted_at || inspection.created_at || new Date()), 'MMMM d, yyyy')}
          </p>
        </div>

        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl print:text-lg">{inspection.template?.name}</CardTitle>
                <p className="text-muted-foreground mt-1 print:hidden">Inspection Report</p>
              </div>
              <Badge variant={inspection.status === 'submitted' ? 'default' : 'secondary'} className="text-sm">
                {inspection.status === 'submitted' ? 'Submitted' : 'Draft'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm inspection-meta-grid">
              <div>
                <p className="text-muted-foreground text-xs">Site</p>
                <p className="font-medium">{inspection.site?.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Area</p>
                <p className="font-medium">{inspection.area || 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Inspector</p>
                <p className="font-medium">{inspection.inspector?.full_name || 'Unknown'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Date</p>
                <p className="font-medium">
                  {format(new Date(inspection.submitted_at || inspection.created_at || new Date()), 'MMM d, yyyy h:mm a')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary with Charts */}
        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center inspection-summary-stats">
              <div className="p-4 bg-muted rounded-lg print:p-2">
                <p className="text-2xl font-bold print:text-lg">{totalItems}</p>
                <p className="text-sm text-muted-foreground print:text-xs">Total Items</p>
              </div>
              <div className="p-4 bg-green-100 dark:bg-green-900/20 rounded-lg print:p-2">
                <p className="text-2xl font-bold text-green-600 print:text-lg">{passCount}</p>
                <p className="text-sm text-muted-foreground print:text-xs">Passed</p>
              </div>
              <div className="p-4 bg-red-100 dark:bg-red-900/20 rounded-lg print:p-2">
                <p className="text-2xl font-bold text-destructive print:text-lg">{failCount}</p>
                <p className="text-sm text-muted-foreground print:text-xs">Failed</p>
              </div>
              <div className="p-4 bg-muted rounded-lg print:p-2">
                <p className="text-2xl font-bold print:text-lg">{naCount}</p>
                <p className="text-sm text-muted-foreground print:text-xs">N/A</p>
              </div>
              <div className="p-4 bg-primary/10 rounded-lg print:p-2">
                <p className="text-2xl font-bold text-primary print:text-lg">{score}%</p>
                <p className="text-sm text-muted-foreground print:text-xs">Score</p>
              </div>
            </div>

            {failCount > 0 && (
              <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 print:p-2 print:mt-2">
                <AlertTriangle className="h-5 w-5 text-destructive print:h-4 print:w-4" />
                <span className="text-sm font-medium print:text-xs">{failCount} item(s) require attention</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Charts - Hidden in print */}
        <div className="grid gap-6 md:grid-cols-2 print:hidden">
          {/* Pass/Fail Pie Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Results Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                  No responses recorded
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section Breakdown Bar Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Results by Section</CardTitle>
            </CardHeader>
            <CardContent>
              {sectionData.length > 0 ? (
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sectionData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={80}
                        tick={{ fontSize: 10 }}
                      />
                      <Tooltip 
                        formatter={(value, name) => [value, name === 'pass' ? 'Passed' : 'Failed']}
                        labelFormatter={(label) => sectionData.find(s => s.name === label)?.fullName || label}
                      />
                      <Bar dataKey="pass" stackId="a" fill="#16a34a" name="Passed" />
                      <Bar dataKey="fail" stackId="a" fill="#dc2626" name="Failed" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                  No section data
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Failed Items Summary */}
        {failedItems.length > 0 && (
          <Card className="border-destructive/50 inspection-failed-items">
            <CardHeader className="print:py-2">
              <CardTitle className="flex items-center gap-2 text-destructive print:text-base">
                <AlertTriangle className="h-5 w-5 print:h-4 print:w-4" />
                Failed Items Requiring Attention ({failedItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="print:py-2">
              <div className="space-y-3 print:space-y-2">
                {failedItems.map((item, index) => (
                  <div key={index} className="p-3 bg-destructive/5 border border-destructive/20 rounded-lg inspection-item inspection-item-fail print:p-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground print:text-xs">{item.section}</p>
                        <p className="font-medium print:text-sm">{item.item}</p>
                        {item.notes && (
                          <p className="text-sm mt-1 print:text-xs"><span className="font-medium">Notes:</span> {item.notes}</p>
                        )}
                      </div>
                      <Badge variant="destructive">FAIL</Badge>
                    </div>
                    {item.photos && item.photos.length > 0 && (
                      <div className="flex gap-2 mt-2 print:hidden">
                        {item.photos.map((url, i) => (
                          <img key={i} src={url} alt={`Evidence ${i + 1}`} className="w-16 h-16 object-cover rounded border" />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detailed Results */}
        <Card>
          <CardHeader className="print:py-2">
            <CardTitle className="print:text-base">Detailed Results</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 print:space-y-3 print:py-2">
            {sortedSections.map((section) => (
              <div key={section.id} className="no-page-break">
                <h3 className="font-semibold text-lg mb-3 inspection-section-header print:text-sm print:mb-2 print:p-2 print:bg-muted">
                  {section.name}
                  {section.subsection && <span className="text-muted-foreground font-normal"> / {section.subsection}</span>}
                </h3>
                
                <div className="space-y-3 print:space-y-1">
                  {getSectionItems(section.id).map((item, index) => {
                    const response = getResponseForItem(item.id);
                    const isFail = response?.response_value === 'FAIL' || response?.response_value === 'NO';

                    return (
                      <div 
                        key={item.id} 
                        className={`p-3 rounded-lg border inspection-item print:p-2 print:rounded-none ${isFail ? 'border-destructive bg-destructive/5 inspection-item-fail' : 'border-border'}`}
                      >
                        <div className="flex items-start justify-between gap-4 print:gap-2">
                          <div className="flex items-start gap-3 print:gap-2">
                            <span className="text-sm text-muted-foreground min-w-[2rem] print:text-xs print:min-w-[1.5rem]">
                              {item.item_code || `${index + 1}.`}
                            </span>
                            <div>
                              <p className="font-medium print:text-sm">{item.item_text}</p>
                              {response?.notes && (
                                <p className="text-sm text-muted-foreground mt-1 print:text-xs print:mt-0">
                                  <span className="font-medium">Notes:</span> {response.notes}
                                </p>
                              )}
                              {response?.photo_urls && response.photo_urls.length > 0 && (
                                <div className="flex gap-2 mt-2 print:hidden">
                                  {response.photo_urls.map((url, i) => (
                                    <img key={i} src={url} alt={`Photo ${i + 1}`} className="w-16 h-16 object-cover rounded" />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {renderResponseBadge(response?.response_value || null)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Separator className="mt-6 print:mt-2" />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground print:mt-8">
          <p>Generated on {format(new Date(), 'MMMM d, yyyy')} at {format(new Date(), 'h:mm a')}</p>
        </div>
      </div>
    </div>
  );
};
